import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MinhaAreaPageShell } from "@/components/MinhaAreaPageShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Bell, 
  BellRing, 
  Check, 
  CheckCheck, 
  Calendar, 
  MessageCircle, 
  Home, 
  AlertCircle,
  Info,
  X
} from "lucide-react";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuthState } from "@/hooks/useAuthState";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import { Notification } from "@/integrations/mysql/types";

const Notifications = () => {
  const navigate = useNavigate();
  const { isAuthenticated, userType, userId, user, loading: authLoading } = useAuthState();
  const { toast } = useToast();
  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    hasUnreadNotifications
  } = useNotifications();

  const [selectedTab, setSelectedTab] = useState("unread");
  const [selectedType, setSelectedType] = useState<string>("all");

  // Opções de filtro por tipo
  const typeOptions = [
    { value: "all", label: "Todos os tipos" },
    { value: "booking_request", label: "Solicitações de Reserva" },
    { value: "booking_confirmed", label: "Reservas Confirmadas" },
    { value: "booking_cancelled", label: "Reservas Canceladas" },
    { value: "booking_rejected", label: "Reservas Rejeitadas" },
    { value: "message_received", label: "Mensagens Recebidas" },
    { value: "system_announcement", label: "Anúncios do Sistema" },
    { value: "general_info", label: "Informações Gerais" }
  ];

  // Aguardar carregamento da autenticação
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando notificações...</p>
    </div>
    </div>
    );
  }

  // Redirecionar se não estiver autenticado
  if (!isAuthenticated) {
    navigate("/login");
    return null;
  }

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'booking_request':
      case 'booking_confirmed':
      case 'booking_cancelled':
      case 'booking_rejected':
        return <Calendar className="h-5 w-5" />;
      case 'message_received':
        return <MessageCircle className="h-5 w-5" />;
      case 'system_announcement':
        return <AlertCircle className="h-5 w-5" />;
      case 'general_info':
        return <Info className="h-5 w-5" />;
      default:
        return <Bell className="h-5 w-5" />;
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'booking_confirmed':
        return 'text-green-600 bg-green-100';
      case 'booking_cancelled':
      case 'booking_rejected':
        return 'text-red-600 bg-red-100';
      case 'booking_request':
        return 'text-blue-600 bg-blue-100';
      case 'message_received':
        return 'text-purple-600 bg-purple-100';
      case 'system_announcement':
        return 'text-orange-600 bg-orange-100';
      case 'general_info':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getNotificationTypeLabel = (type: Notification['type']) => {
    switch (type) {
      case 'booking_request':
        return 'Solicitação de Reserva';
      case 'booking_confirmed':
        return 'Reserva Confirmada';
      case 'booking_cancelled':
        return 'Reserva Cancelada';
      case 'booking_rejected':
        return 'Reserva Rejeitada';
      case 'message_received':
        return 'Nova Mensagem';
      case 'system_announcement':
        return 'Anúncio do Sistema';
      case 'general_info':
        return 'Informação Geral';
      default:
        return 'Notificação';
    }
  };

  const handleMarkAsRead = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
      toast({
        title: "Notificação marcada como lida",
        description: "A notificação foi marcada como lida.",
      });
    }
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    toast({
      title: "Todas as notificações marcadas como lidas",
      description: "Todas as suas notificações foram marcadas como lidas.",
    });
  };


  const handleNotificationClick = async (notification: Notification) => {
    // Marcar como lida se não estiver lida
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navegar para a página relacionada baseada no tipo de notificação e usuário
    if (notification.related_entity_type === 'booking') {
      // Determinar o contexto da notificação baseado no tipo e conteúdo
      if (notification.type === 'booking_request') {
        // Se é uma solicitação de reserva, verificar se é para hóspede ou proprietário
        if (notification.title.includes('Solicitação Enviada') || 
            notification.message.includes('sua solicitação')) {
          // É uma notificação para o hóspede (quem fez a solicitação)
          navigate('/minha-area/reservas');
        } else if (notification.title.includes('Nova Solicitação') || 
                   notification.message.includes('solicitação de reserva')) {
          // É uma notificação para o proprietário (quem recebeu a solicitação)
          navigate('/minha-area/solicitacoes-reservas');
        } else {
          // Fallback para reservas
          navigate('/minha-area/reservas');
        }
      } else if (notification.type === 'booking_confirmed' || 
                 notification.type === 'booking_cancelled' || 
                 notification.type === 'booking_rejected') {
        // Notificações de status de reserva sempre vão para "Minhas Reservas"
        navigate('/minha-area/reservas');
      } else {
        // Fallback para outras notificações de booking
        navigate('/minha-area/reservas');
      }
    } else if (notification.related_entity_type === 'message') {
      navigate('/minha-area/mensagens');
    } else if (notification.related_entity_type === 'property') {
      navigate(`/imovel/${notification.related_entity_id}`);
    }
  };

  // Função para filtrar notificações por tipo
  const filterByType = (notification: Notification) => {
    if (selectedType === "all") return true;
    return notification.type === selectedType;
  };

  // Função para filtrar notificações por status (lida/não lida)
  const filterByStatus = (notification: Notification) => {
    if (selectedTab === "unread") {
      return !notification.is_read;
    }
    return true;
  };

  // Aplicar ambos os filtros
  const filteredNotifications = notifications.filter(notification => 
    filterByType(notification) && filterByStatus(notification)
  );

  const unreadNotifications = notifications.filter(n => !n.is_read);

  return (
    <MinhaAreaPageShell>
<div className="w-full">
        <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <BellRing className="h-6 w-6 text-primary" />
    </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Notificações</h1>
                  <p className="text-muted-foreground">
                    {unreadCount > 0 
                      ? `${unreadCount} notificação${unreadCount > 1 ? 'ões' : ''} não lida${unreadCount > 1 ? 's' : ''}`
                      : "Todas as notificações foram lidas"
                    }
                  </p>
    </div>
    </div>
              
              {hasUnreadNotifications && (
                <Button onClick={handleMarkAllAsRead} variant="outline" size="sm">
                  <CheckCheck className="h-4 w-4 mr-2" />
                  Marcar todas como lidas
                </Button>
              )}
    </div>
    </div>

          {/* Tabs */}
          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="mb-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="unread" className="flex items-center gap-2">
                <BellRing className="h-4 w-4" />
                Não lidas ({unreadNotifications.length})
              </TabsTrigger>
              <TabsTrigger value="all" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Todas ({notifications.length})
              </TabsTrigger>
            </TabsList>

            {/* Filtro por tipo */}
            <div className="mt-4 flex items-center gap-4">
              <label className="text-sm font-medium text-foreground">
                Filtrar por tipo:
              </label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
    </div>

            <TabsContent value="all" className="mt-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-2 text-muted-foreground">Carregando notificações...</span>
    </div>
              ) : error ? (
                <Card>
                  <CardContent className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Erro ao carregar notificações</h3>
                      <p className="text-muted-foreground mb-4">{error}</p>
                      <Button onClick={() => window.location.reload()}>
                        Tentar novamente
                      </Button>
    </div>
                  </CardContent>
                </Card>
              ) : filteredNotifications.length === 0 ? (
                <Card>
                  <CardContent className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Nenhuma notificação</h3>
                      <p className="text-muted-foreground">
                        Você não possui notificações no momento.
                      </p>
    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {filteredNotifications.map((notification) => (
                    <Card 
                      key={notification.id} 
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        !notification.is_read 
                          ? 'border-primary bg-primary/5 shadow-sm' 
                          : 'border-border'
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${getNotificationColor(notification.type)}`}>
                            {getNotificationIcon(notification.type)}
    </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <h3 className={`font-semibold ${
                                  !notification.is_read ? 'text-primary' : 'text-foreground'
                                }`}>
                                  {notification.title}
                                </h3>
                                {!notification.is_read && (
                                  <Badge variant="secondary" className="text-xs">
                                    Nova
                                  </Badge>
                                )}
    </div>
                              
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {getNotificationTypeLabel(notification.type)}
                                </Badge>
                                
    </div>
    </div>
                            
                            <p className="text-muted-foreground mb-3 line-clamp-2">
                              {notification.message}
                            </p>
                            
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                              <span>
                                {format(new Date(notification.created_at), "dd/MM/yyyy 'às' HH:mm", { 
                                  locale: ptBR,
                                  timeZone: 'America/Sao_Paulo'
                                })}
                              </span>
                              
                              {!notification.is_read && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(notification);
                                  }}
                                  className="text-primary hover:text-primary"
                                >
                                  <Check className="h-4 w-4 mr-1" />
                                  Marcar como lida
                                </Button>
                              )}
    </div>
    </div>
    </div>
                      </CardContent>
                    </Card>
                  ))}
    </div>
              )}
            </TabsContent>

            <TabsContent value="unread" className="mt-6">
              {unreadNotifications.length === 0 ? (
                <Card>
                  <CardContent className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <CheckCheck className="h-12 w-12 text-green-600 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Todas as notificações foram lidas</h3>
                      <p className="text-muted-foreground">
                        Você não possui notificações não lidas no momento.
                      </p>
    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {unreadNotifications.map((notification) => (
                    <Card 
                      key={notification.id} 
                      className="cursor-pointer transition-all hover:shadow-md border-primary bg-primary/5 shadow-sm"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`p-2 rounded-lg ${getNotificationColor(notification.type)}`}>
                            {getNotificationIcon(notification.type)}
    </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-primary">
                                  {notification.title}
                                </h3>
                                <Badge variant="secondary" className="text-xs">
                                  Nova
                                </Badge>
    </div>
                              
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  {getNotificationTypeLabel(notification.type)}
                                </Badge>
                                
    </div>
    </div>
                            
                            <p className="text-muted-foreground mb-3 line-clamp-2">
                              {notification.message}
                            </p>
                            
                            <div className="flex items-center justify-between text-sm text-muted-foreground">
                              <span>
                                {format(new Date(notification.created_at), "dd/MM/yyyy 'às' HH:mm", { 
                                  locale: ptBR,
                                  timeZone: 'America/Sao_Paulo'
                                })}
                              </span>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead(notification);
                                }}
                                className="text-primary hover:text-primary"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Marcar como lida
                              </Button>
    </div>
    </div>
    </div>
                      </CardContent>
                    </Card>
                  ))}
    </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
    </MinhaAreaPageShell>
  );
};

export default Notifications;
